import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Calendar, MapPin, Clock, Gift } from "lucide-react";
import { Badge } from "../../../ui/badge.jsx";

import { formatEventDate } from "../../../../Utils/FormatEventDate.jsx";
import CustomDatePicker from "../../../ui/DatePicker.jsx";
import ActionButtons from "../molecules/ActionButtons.jsx";
import useEditableCollection from "../../../../Hooks/useEditableCollection.js";
import "../../../Styles/DatePicker.scss";
import ColoredGiftBg from "../../../../Assets/Images/ColoredGiftBg.png";
import ColoredGiftBgInverse from "../../../../Assets/Images/ColoredGiftBgInverse.png";

// Atomic Components
import CardContainer from "../../../ui/Cards/CardContainer.jsx";
import CardHeader from "../../../ui/Cards/CardHeader.jsx";
import BadgeOverlay from "../../../ui/Cards/BadgeOverlay.jsx";
import CardContent from "../../../ui/Cards/CardContent.jsx";
import CardActions from "../../../ui/Cards/CardActions.jsx";
import { useTranslation } from "react-i18next";

const EventCard = ({ eventData, onDelete, onRedirect }) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [localEventData, setLocalEventData] = useState(eventData);
  const [isHovered, setIsHovered] = useState(false);

  const {
    isEditing,
    editedData,
    setEditedData,
    handleEdit,
    handleInputChange,
    setIsEditing,
  } = useEditableCollection("events", eventData);

  const type = "events";

  useEffect(() => {
    setLocalEventData(eventData);
  }, [eventData]);

  const eventImage = isHovered ? ColoredGiftBgInverse : ColoredGiftBg;

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.();
  };

  const handleRedirect = () => {
    if (!isEditing) {
      onRedirect?.(type, localEventData.giftID, localEventData.eventName);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditedData({
      eventName: localEventData.eventName || "",
      eventDescription: localEventData.eventDescription || "",
      eventDate: localEventData.eventDate || "",
    });
  };

  const onSave = async (e) => {
    const response = await handleEdit(e);
    if (response && response.updatedData) {
      setLocalEventData((prev) => ({
        ...prev,
        ...response.updatedData,
        eventName: response.eventName || response.updatedData.eventName || prev.eventName,
        eventDate: response.eventDate || response.updatedData.eventDate || prev.eventDate,
        eventDescription: response.eventDescription || response.updatedData.eventDescription || prev.eventDescription,
      }));
    }
  };

  return (
    <CardContainer
      variant="event"
      onClick={handleRedirect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardHeader
        image={eventImage}
        alt={`${localEventData.eventName} event`}
        onImageClick={handleRedirect}
        onImageLoad={() => setImageLoaded(true)}
        imageLoaded={imageLoaded}
      >
        {localEventData.giftID && localEventData.giftID.length > 0 && (
          <BadgeOverlay position="top-right">
            <Badge variant="default" size="md">
              <Gift className="h-3.5 w-3.5" />
              <span>{localEventData.giftID.length}</span>
            </Badge>
          </BadgeOverlay>
        )}

        <BadgeOverlay position="bottom-left">
          <Badge
            variant={localEventData.featured ? "ColorGreen" : "primary"}
            size="sm"
          >
            {localEventData.featured ? t("eventsAndSpaces.featured") : (localEventData.category ? t(`events.${localEventData.category.charAt(0).toLowerCase() + localEventData.category.slice(1).replace(/\s+/g, '')}Cat`) : t("eventsAndSpaces.eventLabel"))}
          </Badge>
        </BadgeOverlay>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between mt-3 mb-2">
          <div className="flex items-center text-gray-600 text-xs">
            <Calendar className="h-4 w-4 mr-1 text-orange-500" />
            <span className="font-medium">
              {isEditing ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <CustomDatePicker
                    selected={new Date(editedData.eventDate)}
                    onChange={(date) => {
                      setEditedData((prev) => ({
                        ...prev,
                        eventDate: date.toISOString(),
                      }));
                    }}
                    minDate={new Date()}
                    className="w-full border border-gray-300 rounded-md p-1 text-xs"
                    portalId="datepicker-portal"
                    withPortal
                  />
                </div>
              ) : (
                formatEventDate(localEventData.eventDate)
              )}
            </span>
          </div>
        </div>

        {isEditing ? (
          <div onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              name="eventName"
              value={editedData.eventName}
              onChange={handleInputChange}
              className="text-base font-bold mb-2 w-full bg-transparent border-b-2 border-orange-400 focus:outline-none focus:border-red-500 transition-colors"
              placeholder={t("eventsAndSpaces.eventName")}
            />
          </div>
        ) : (
          <h2
            className="text-base font-bold mb-2 text-gray-800 line-clamp-2 transition-colors cursor-pointer"
            onClick={handleRedirect}
          >
            {localEventData.eventName}
          </h2>
        )}

        {isEditing ? (
          <div onClick={(e) => e.stopPropagation()}>
            <textarea
              name="eventDescription"
              placeholder={t("eventsAndSpaces.enterEventDesc")}
              value={editedData.eventDescription}
              onChange={handleInputChange}
              className="w-full bg-white/50 border border-gray-300 rounded-md p-2 mt-2 text-xs font-normal leading-relaxed resize-none"
              rows={3}
            />
          </div>
        ) : (
          <p className="text-gray-700 mb-3 text-xs leading-relaxed font-normal line-clamp-3">
            {localEventData.eventDescription
              ? localEventData.eventDescription
              : t("eventsAndSpaces.noDescProvided")}
          </p>
        )}
      </CardContent>

      <CardActions isEditing={isEditing}>
        <ActionButtons
          isEditing={isEditing}
          onEdit={onSave}
          onDelete={handleDelete}
          onCancel={handleCancel}
        />
      </CardActions>
    </CardContainer>
  );
};

export default EventCard;
